import { map as Rmap } from "ramda";
import api from "@/api";
import { find as Rfind, propEq as RpropEq } from "ramda";
import { JSConn } from "@/utilities/js-conn";

interface LineItem {
  id: string;
  description: string;
  default_rate: number | string;
  status: string;
}

interface LineItemState {
  lineItems: LineItem[];
  lineItem: LineItem | {};
}

const emptyLineItem: LineItem = {
  id: "",
  description: "",
  default_rate: 0,
  status: "active",
};

const itemDetails = {
  pluralName: "line_items",
  singularName: "line_item",
};

const apiParamsBuilder = (jsConn: JSConn) => {
  return {
    id: jsConn.formParams.id,
    line_item: {
      ...jsConn.formParams,
    },
  };
};

const state: LineItemState = {
  lineItems: [],
  lineItem: {},
};

const mutations = {
  LOAD_LINE_ITEMS(state: LineItemState, payload: LineItem[]) {
    state.lineItems = payload;
  },
  LOAD_LINE_ITEM(state: LineItemState, payload: LineItem) {
    state.lineItem = payload;
  },
};

const actions = {
  needLineItems(context: any, jsConn: JSConn) {
    jsConn.apiParams = jsConn.formParams;
    return api.getItems(jsConn, itemDetails).then(jsConn => {
      context.commit("LOAD_LINE_ITEMS", jsConn.response);
    });
  },
  needLineItem(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);
    return api.getItem(jsConn, itemDetails).then(jsConn => {
      context.commit("LOAD_LINE_ITEM", jsConn.response);
    });
  },
  createLineItem(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);
    return api
      .createItem(jsConn, itemDetails)
      .then(jsConn => {
        context.commit("LOAD_LINE_ITEM", jsConn.response);
        return Promise.resolve(jsConn);
      })
      .catch(jsConn => {
        return Promise.reject(jsConn);
      });
  },
  updateLineItem(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);
    return api
      .updateItem(jsConn, itemDetails)
      .then(jsConn => {
        context.commit("LOAD_LINE_ITEM", jsConn.response);
        return Promise.resolve(jsConn);
      })
      .catch(jsConn => {
        return Promise.reject(jsConn);
      });
  },
  deleteLineItem(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);

    return api
      .deleteItem(jsConn, itemDetails)
      .then(jsConn => {
        context.commit("LOAD_LINE_ITEM", emptyLineItem);
        return Promise.resolve(jsConn);
      })
      .catch(jsConn => {
        return Promise.reject(jsConn);
      });
  },
};

const getters = {
  lineItems: (state: LineItemState) => {
    return state.lineItems;
  },
  lineItem: (state: LineItemState) => {
    return state.lineItem;
  },
  lineItemsForTable: (state: LineItemState) => {
    return state.lineItems;
  },
  lineItemsForAutocomplete: (state: LineItemState) => {
    // map over the items and just return a label and an id
    return Rmap(lineItem => {
      return {
        value: lineItem.id,
        label: lineItem.description,
      };
    }, state.lineItems);
  },
  lineItemById: (state: LineItemState) => (id: string) => {
    return Rfind(lnItem => {
      return RpropEq("id", id, lnItem);
    }, state.lineItems);
  },
};

const lineItemsModule = {
  state,
  mutations,
  actions,
  getters,
};

export default lineItemsModule;
